import React from 'react';
import { Button, theme } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import HallazgoRow from './HallazgoRow';
import { getHallazgoId } from 'utils/multiHallazgo';

// Lista densa de hallazgos de un ítem del checklist (Variante B): N incidencias
// + N sugerencias, cada una con su fila compacta. El estado vive en el provider
// (obsSug[idRes]); este componente solo renderiza y emite callbacks.
function HallazgoListEditor({
  incidencias = [],
  sugerencias = [],
  onAdd,
  onUpdate,
  onRemove,
  mantencionId,
  hallazgosPorActRes,
  isPro,
  equipoId,
  saveTick,
  showIncidencias = true,
  showSugerencias = true,
  showFotos = true,
  showSeveridad = true,
  autoFocusTempId = null,
  // ¿El tenant exige severidad en toda incidencia con texto? Se reenvía tal
  // cual a cada fila de incidencia (ver comentario en HallazgoRow).
  exigeSeveridad = false,
}) {
  const { token } = theme.useToken();

  // Gate real por-ítem: basta con que UNA incidencia tenga texto. Una fila
  // vacía solo "bloquea" (rojo + aviso) cuando ninguna incidencia hermana lo
  // tiene — si no, el cierre ya está habilitado y mostrarla en rojo sería un
  // falso aviso (ver Hallazgo 1 de la revisión: la fila sembrada al tocar
  // "+ Incidencia" tras ya describir la primera no debe asustar al técnico).
  const hayIncidenciaConTexto = incidencias.some((h) => String(h.descripcion || '').trim() !== '');

  const renderRows = (lista, tipo) =>
    lista.map((h) => (
      <HallazgoRow
        key={h.tempId}
        tipo={tipo}
        hallazgo={h}
        autoFocus={tipo === 'INCIDENCIA' && h.tempId === autoFocusTempId}
        idHallazgoResuelto={getHallazgoId(hallazgosPorActRes, h.tempId)}
        onUpdate={(patch) => onUpdate(tipo, h.tempId, patch)}
        onRemove={() => onRemove(tipo, h.tempId)}
        mantencionId={mantencionId}
        isPro={isPro}
        equipoId={equipoId}
        saveTick={saveTick}
        showFotos={showFotos}
        showSeveridad={showSeveridad}
        descripcionBloquea={!hayIncidenciaConTexto}
        exigeSeveridad={exigeSeveridad}
      />
    ));

  const addBtnStyle = {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Incidencias — solo cuando el ítem está "Con problema" (RECHAZADO) */}
      {showIncidencias && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {renderRows(incidencias, 'INCIDENCIA')}
          <div>
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAdd('INCIDENCIA')}
              style={{
                ...addBtnStyle,
                color: token.colorError,
                borderColor: token.colorErrorBorder,
              }}
            >
              Incidencia
            </Button>
          </div>
        </div>
      )}

      {/* Sugerencias */}
      {showSugerencias && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {renderRows(sugerencias, 'SUGERENCIA')}
          <div>
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => onAdd('SUGERENCIA')}
              style={{
                ...addBtnStyle,
                color: token.colorWarning,
                borderColor: token.colorWarningBorder,
              }}
            >
              Sugerencia
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HallazgoListEditor;
